import { classnames, ColumnSize, getColumnWidth } from '@aibees/shared/utils';
import { useTheme } from '@mui/material';
import { mergeSx } from 'merge-sx';
import { useEffect, useRef } from 'react';
import { SELECTABLE_COLUMN_WIDTH } from '../../constant';
import { useAdvanceTableStickyContext } from '../../context';
import { useCollapsedVisibility } from '../../hooks/use-collapsed-visibility';
import { AdvanceTableColumn } from '../../types';
import * as Styled from './styles';
import * as SX from './sx';
import type { TableStickyRowProps as Props } from './types';

export const TableStickyRow = ({
  className,
  item,
  index,
  selected,
  config,
  columns,
  checkboxEnabled,
  onSelect,
  onRowClick,
  ...other
}: Props) => {
  const { stickyCollapsed, setStickyRowHeight } =
    useAdvanceTableStickyContext();
  const { hideInCollapsed, showInCollapsed } = useCollapsedVisibility(
    stickyCollapsed,
    index
  );

  const theme = useTheme();

  const hasStickyCol = columns.some((i) => i.isSticky);

  const render = (col: AdvanceTableColumn) =>
    (col.render
      ? col.render(item[col.id as string], item, index)
      : item[col.id as string]) ?? '-';

  const selectCheckBox = (
    <Styled.SelectBox
      checked={selected}
      onChange={() => {
        onSelect?.(item.id);
      }}
    />
  );

  const rowRef = useRef<HTMLTableRowElement>(null);

  const calculatedMinWidth = (size?: ColumnSize) => {
    return size ? getColumnWidth(size) : 'auto';
  };

  useEffect(() => {
    if (rowRef.current) {
      // Calculate and set the height of the TableRow
      setStickyRowHeight(rowRef.current.offsetHeight);
    }
  }, []); // Runs once after the component mounts

  return (
    <Styled.Wrapper
      className={classnames('TableStickyRow', className, {
        showInCollapsed,
        hideInCollapsed,
        stickyCollapsed: !stickyCollapsed,
      })}
      {...other}
      onClick={onRowClick}
      ref={rowRef}
    >
      {config.rowSelectable && (
        <Styled.CheckboxCell
          className="TableRowSelectCell"
          onClick={(e) => e.stopPropagation()}
          sx={{
            ...(hasStickyCol && {
              position: 'sticky',
              left: 0,
              background: theme.palette.primary[50],
              zIndex: 99,
            }),
          }}
        >
          {selectCheckBox}
        </Styled.CheckboxCell>
      )}

      {columns.map((col) => {
        const sx = mergeSx(SX.cell(col), config.cellSx, {
          maxWidth: calculatedMinWidth(col.size),
          ...(col.isSticky && {
            position: 'sticky',
            left: SELECTABLE_COLUMN_WIDTH,
            background: theme.palette.primary[50],
            boxShadow: '4px -3px 8px -5px #00000066',
            borderWidth: '1px',
            borderColor: theme.palette.primary[500],
            borderStyle: 'solid',
            zIndex: 99,
          }),
        });

        return (
          <Styled.Cell
            key={String(col.key ?? col.id)}
            data-id={col.key ?? col.id}
            sx={sx}
          >
            {render(col)}
          </Styled.Cell>
        );
      })}
    </Styled.Wrapper>
  );
};

TableStickyRow.displayName = 'TableStickyRow';
